import type { HttpClientInstance } from '@thehotelsnetwork/http-client';

import type { User, UserIntercomCredentials } from '../types';

import { accountUserExists } from '@/api/routes/accounts';
import {
  userChangePassword,
  userDetails,
  userIntercomCredentials,
  userUpdateProfile,
} from '@/api/routes/users';

export interface UserApiDeps {
  deps: {
    apiClient: HttpClientInstance;
  };
}

export interface FetchUserDetailsParams extends UserApiDeps {
  userId: string;
}

export interface ChangePasswordParams extends UserApiDeps {
  oldPassword: string;
  newPassword: string;
}

export interface UpdateProfileParams extends UserApiDeps {
  profile: Partial<User> & { id: string };
}

export interface CheckUserExistsParams extends UserApiDeps {
  accountId: string;
  email: string;
}

export interface FetchIntercomCredentialsParams extends UserApiDeps {
  accountId: string;
}

export async function fetchUserDetails({
  userId,
  deps,
}: FetchUserDetailsParams): Promise<User | null> {
  try {
    const { data } = await deps.apiClient.request<User>({
      method: 'GET',
      url: userDetails,
      pathParams: {
        id: userId,
      },
    });

    return data;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(error);
    return null;
  }
}

export async function changePassword({
  oldPassword,
  newPassword,
  deps,
}: ChangePasswordParams): Promise<void> {
  await deps.apiClient.request({
    method: 'POST',
    url: userChangePassword,
    data: {
      old_password: oldPassword,
      new_password: newPassword,
    },
  });
}

export async function updateProfile({
  profile,
  deps,
}: UpdateProfileParams): Promise<void> {
  await deps.apiClient.request({
    method: 'PUT',
    url: userUpdateProfile,
    pathParams: {
      id: profile.id,
    },
    data: {
      ...profile,
    },
  });
}

export async function checkUserExists({
  accountId,
  email,
  deps,
}: CheckUserExistsParams): Promise<{ exists: boolean }> {
  const { data } = await deps.apiClient.request<{ exists: boolean }>({
    method: 'GET',
    url: accountUserExists,
    pathParams: {
      id: accountId,
    },
    params: {
      email,
    },
  });

  return data;
}

export async function fetchIntercomCredentials({
  accountId,
  deps,
}: FetchIntercomCredentialsParams): Promise<UserIntercomCredentials> {
  const { data } = await deps.apiClient.request<UserIntercomCredentials>({
    method: 'GET',
    url: userIntercomCredentials,
    headers: {
      'x-thn-account-id': accountId,
    },
  });

  return data;
}
